import React, { useState, useEffect } from 'react';
import { CloseOutlined, CalendarOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useI18n } from 'hooks/useI18n';
import './subscription-expiry-warning.scss';

/**
 * SubscriptionExpiryWarning Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.nextBillDate - The next bill date in ISO string format
 * @param {number} [props.warningThresholdDays=30] - Number of days before expiry to show warning (default: 30)
 * 
 * @example
 * // Default: Shows warning when 30 days or less remaining
 * <SubscriptionExpiryWarning nextBillDate={loginRedux?.nextBillDate} />
 * 
 * @example
 * // Custom: Shows warning when 15 days or less remaining
 * <SubscriptionExpiryWarning nextBillDate={loginRedux?.nextBillDate} warningThresholdDays={15} />
 */

// Function to calculate remaining days
const calculateRemainingDays = (nextBillDate) => {
	if (!nextBillDate) return null;
	
	const currentDate = new Date();
	const billDate = new Date(nextBillDate);
	
	if (isNaN(billDate.getTime())) return null;
	
	const timeDifference = billDate.getTime() - currentDate.getTime();
	const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
	
	return daysDifference;
};

// Format date with locale support
const formatDate = (dateString) => {
	if (!dateString) return '';
	const date = new Date(dateString);
	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	});
};

const SubscriptionExpiryWarning = ({ nextBillDate, warningThresholdDays = 30 }) => {
	const { t } = useI18n();
	const [isDismissed, setIsDismissed] = useState(false);
	
	const remainingDays = calculateRemainingDays(nextBillDate);
	
	// Create unique localStorage key based on threshold to avoid conflicts
	const localStorageKey = `subscription-warning-dismissed-${warningThresholdDays}`;
	
	// Check localStorage on mount
	useEffect(() => {
		const dismissed = localStorage.getItem(localStorageKey);
		if (dismissed === 'true') {
			setIsDismissed(true);
		}
	}, [localStorageKey]);
	
	// Don't show if:
	// - Already dismissed
	// - No nextBillDate
	// - More than threshold days remaining
	// - Already expired (negative days)
	if (isDismissed || !nextBillDate || !remainingDays || remainingDays > warningThresholdDays || remainingDays < 0) {
		return null;
	}
	
	const handleDismiss = () => {
		setIsDismissed(true);
		localStorage.setItem(localStorageKey, 'true');
	};
	
	// Determine alert type and message based on remaining days
	const getAlertType = () => {
		if (remainingDays <= 7) return 'error';
		if (remainingDays <= 15) return 'warning';
		return 'info';
	};
	
	const getStatusMessage = () => {
		if (remainingDays === 0) return t('dashboard.subscription_expires_today');
		if (remainingDays === 1) return t('dashboard.subscription_expires_tomorrow');
		if (remainingDays <= 7) return t('dashboard.subscription_expiring_soon');
		return t('dashboard.subscription_expiring_in_days', { days: remainingDays });
	};
	
	const urgency = getAlertType();
	
	return (
		<div className="subscription-expiry-warning">
			<div className="subscription-alert-card" data-urgency={urgency}>
				<div className="subscription-alert-content">
					<div className="subscription-alert-left">
						<div className="subscription-alert-icon-wrapper">
							{remainingDays <= 7 ? (
								<ExclamationCircleOutlined className="subscription-alert-icon" />
							) : (
								<CalendarOutlined className="subscription-alert-icon" />
							)}
						</div>
						<div className="subscription-alert-text-content">
							<div className="subscription-alert-title">
								{getStatusMessage()}
							</div>
							<div className="subscription-alert-description">
								{t('dashboard.subscription_expiry_description')} <span className="subscription-date-highlight">{formatDate(nextBillDate)}</span>
							</div>
						</div>
					</div>
					<button
						type="button"
						onClick={handleDismiss}
						className="subscription-dismiss-btn"
						aria-label={t('dashboard.dismiss')}
					>
						<CloseOutlined />
					</button>
				</div>
			</div>
		</div>
	);
};

export default SubscriptionExpiryWarning;

